import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

// Imported from the file, not the `@/pages/knowledge-base` barrel: the overlay
// is mounted app-wide and the barrel pulls in the PDF viewer.
import { DOCUMENT_SEARCH_MIN_QUERY_LENGTH } from '@/constant/documents';
import { useDocuments } from '@/pages/knowledge-base/hooks/useDocuments';
import { documentsApi } from '@/services/documents';
import {
  DocumentationDocument,
  DocumentListType,
  DocumentSearchHit,
  DocumentSearchPage,
} from '@/types/documents';

export const DOCUMENT_SEARCH_QUERY_KEY = 'document-search';

/** Documents asked of Xano. The caller's cap trims this per view. */
export const DOCUMENT_SEARCH_REQUEST_LIMIT = 10;

export interface DocumentSearchResult {
  documentId: string;
  name: string;
  /** From the cached Knowledge Base lists; absent for a semantic-only hit. */
  category?: string;
  /** Shown when no page row survives. Same origin as `category`. */
  summary?: string;
  /**
   * Page rows with a Page Guide, best first, capped. Empty when page search
   * did not run, or when every page hit has no guide: documents indexed
   * before Page Guides existed return hits with an empty `guide`, and a row
   * with nothing to read is noise.
   */
  pages: DocumentSearchPage[];
  /** Page a card click opens: the top-scoring page hit, with a guide or not. */
  bestPage?: number;
  /** `name` comes from the Knowledge Base lists, `semantic` from Xano. */
  source: 'name' | 'semantic';
}

export interface UseDocumentSearchOptions {
  queryText: string;
  enabled?: boolean;
  /** Max documents shown. */
  documentCap: number;
  /** Max of those found by name, so semantic hits keep their slots. */
  nameMatchCap: number;
  /** Max page rows per document. */
  pageCap: number;
}

const byScoreDesc = (a: DocumentSearchPage, b: DocumentSearchPage) =>
  b.score - a.score;

const hasGuide = (page: DocumentSearchPage) =>
  typeof page.guide === 'string' && page.guide.trim().length > 0;

/**
 * Summaries and guides are LLM-written and sometimes arrive as markdown
 * ("**Document Description:** …", "# Heading"). Cards show plain text.
 */
export const stripMarkdown = (value: string): string =>
  value
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}(?:[-*_]\s*){3,}$/gm, ' ')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/\s+/g, ' ')
    .trim();

const blank = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const text = stripMarkdown(value);
  return text.length > 0 ? text : undefined;
};

/** FSI's order: `rank` when it sends one, else the order of the response. */
const byRank = (hits: DocumentSearchHit[]): DocumentSearchHit[] =>
  hits
    .map((hit, index) => ({ hit, index }))
    .sort(
      (a, b) =>
        (a.hit.rank ?? Number.POSITIVE_INFINITY) -
          (b.hit.rank ?? Number.POSITIVE_INFINITY) || a.index - b.index
    )
    .map(({ hit }) => hit);

/** `Boiler manual.pdf` matches on `Boiler manual`, so "pdf" matches nothing. */
const stripExtension = (name: string) => name.replace(/\.[a-z0-9]{2,5}$/i, '');

const nameMatches = (
  documents: DocumentationDocument[],
  needle: string
): DocumentationDocument[] => {
  const lower = needle.toLowerCase();
  return documents.filter(
    doc =>
      doc.status !== 'processing' &&
      typeof doc.name === 'string' &&
      stripExtension(doc.name).toLowerCase().includes(lower)
  );
};

export const mergeDocumentResults = ({
  named,
  semantic,
  known = named,
  documentCap,
  nameMatchCap = documentCap,
  pageCap,
}: {
  named: DocumentationDocument[];
  /** Every semantic hit, in FSI order. */
  semantic: DocumentSearchHit[];
  /** Cached Knowledge Base documents, for category and summary. */
  known?: DocumentationDocument[];
  documentCap: number;
  nameMatchCap?: number;
  pageCap: number;
}): DocumentSearchResult[] => {
  const semanticById = new Map(semantic.map(hit => [hit.documentId, hit]));
  const knownById = new Map(known.map(doc => [doc.id, doc]));
  const seen = new Set<string>();
  const results: DocumentSearchResult[] = [];

  const toResult = (
    documentId: string,
    name: string,
    source: DocumentSearchResult['source'],
    hit?: DocumentSearchHit
  ): DocumentSearchResult => {
    const ranked = [...(hit?.pages ?? [])].sort(byScoreDesc);
    const doc = knownById.get(documentId);
    return {
      documentId,
      name,
      category: blank(doc?.category),
      summary: blank(doc?.summary),
      pages: ranked
        .filter(hasGuide)
        .slice(0, pageCap)
        .map(page => ({ ...page, guide: stripMarkdown(page.guide) })),
      bestPage: ranked[0]?.pageNumber,
      source,
    };
  };

  let nameCount = 0;
  for (const doc of named) {
    if (seen.has(doc.id)) continue;
    if (nameCount >= nameMatchCap) break;
    nameCount += 1;
    seen.add(doc.id);
    // A document in both lists takes its page rows from the semantic hit.
    results.push(toResult(doc.id, doc.name, 'name', semanticById.get(doc.id)));
  }
  for (const hit of byRank(semantic)) {
    if (seen.has(hit.documentId)) continue;
    // No score cut-off here: with page search on, the document `score` is a
    // rank-based RRF number (at most about 0.033), not a relevance score, so
    // it must not be used as a threshold. Page-level `score` values are real
    // hybrid scores. Trust FSI's order and the caps below.
    seen.add(hit.documentId);
    results.push(toResult(hit.documentId, hit.name, 'semantic', hit));
  }

  return results.slice(0, documentCap);
};

export const useDocumentSearch = ({
  queryText,
  enabled = true,
  documentCap,
  nameMatchCap,
  pageCap,
}: UseDocumentSearchOptions) => {
  const trimmed = queryText.trim();
  const isQueryReady = trimmed.length >= DOCUMENT_SEARCH_MIN_QUERY_LENGTH;
  const active = enabled && isQueryReady;

  // Same infinite-query cache as the Knowledge Base page (`useDocuments`), so
  // the shape matches and a warm cache costs nothing. Fetched once the query is
  // long enough to search, not when the overlay opens: `useDocuments` polls
  // every 10s while a document is processing and takes no interval override.
  const { documents: myDocuments } = useDocuments(
    DocumentListType.MyDocuments,
    active
  );
  const { documents: sharedDocuments } = useDocuments(
    DocumentListType.SharedDocuments,
    active
  );

  const query = useQuery({
    queryKey: [
      DOCUMENT_SEARCH_QUERY_KEY,
      trimmed,
      DOCUMENT_SEARCH_REQUEST_LIMIT,
    ],
    queryFn: ({ signal }) =>
      documentsApi.search(trimmed, DOCUMENT_SEARCH_REQUEST_LIMIT, signal),
    enabled: active,
    // A failure hides the section; do not hold the overlay waiting on retries.
    retry: false,
  });

  const semanticHits = useMemo(
    () => (query.data?.status === 'ok' ? (query.data.hits ?? []) : []),
    [query.data]
  );

  const known = useMemo(
    () => [...myDocuments, ...sharedDocuments],
    [myDocuments, sharedDocuments]
  );

  const named = useMemo(
    () => (active ? nameMatches(known, trimmed) : []),
    [active, known, trimmed]
  );

  const results = useMemo(
    () =>
      active
        ? mergeDocumentResults({
            named,
            semantic: semanticHits,
            known,
            documentCap,
            nameMatchCap,
            pageCap,
          })
        : [],
    [active, named, semanticHits, known, documentCap, nameMatchCap, pageCap]
  );

  return {
    results,
    isLoading: active && query.isLoading,
    isFetching: active && query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
};
